import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/shadcn/lib/utils';

const buttonVariants = cva(
  'inline-flex h-full cursor-pointer select-none items-center justify-center gap-2 whitespace-nowrap rounded-sm font-bold text-[11px] uppercase leading-1 tracking-wider ring-offset-background transition-all duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-98 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90 focus-visible:ring-destructive',
        ghostDestructive:
          'text-destructive hover:bg-destructive hover:text-destructive-foreground',
        ghostDestructiveSubtle:
          'font-normal hover:bg-destructive hover:text-destructive-foreground',
        outline:
          'border border-input text-accent-foreground/80 hover:bg-accent hover:text-accent-foreground',
        outlinePrimary:
          'border border-primary font-semibold text-primary hover:bg-primary hover:text-primary-foreground',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        ghostSubtle:
          'border border-border bg-muted text-muted-foreground hover:brightness-90',
        link: 'text-primary normal-case underline-offset-4 hover:underline',
        pushtext:
          'text-primary hover:bg-accent data-[active=true]:pointer-events-none data-[active=true]:text-muted-foreground',
        pushtextMuted:
          'text-muted-foreground hover:bg-accent data-[active=true]:pointer-events-none data-[active=true]:text-muted-foreground'
      },
      size: {
        default: 'h-7 px-4 py-2',
        sm: 'h-6 rounded-md px-2 py-1',
        lg: 'h-11 rounded-md px-8',
        icon: 'h-8 w-8 shrink-0',
        smallIcon: 'h-6 w-6 [&_svg]:size-3.5',
        xsIcon: 'h-5 w-5 [&_svg]:size-3',
        largeIcon: 'size-8 [&_svg]:size-5'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  } & (
    | {
        variant: Extract<
          VariantProps<typeof buttonVariants>['variant'],
          'pushtext'
        >;
        active: boolean;
      }
    | {
        variant?: Exclude<
          VariantProps<typeof buttonVariants>['variant'],
          'pushtext'
        >;
        active?: boolean;
      }
  );

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, active, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        data-active={active}
        ref={ref}
        type="button"
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
